import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { env } from '@dukanti/shared/env';
import { useTranslation, type BillingPlanCatalogItem } from '@dukanti/shared';
import type { ConfirmSubscriptionInput, StartSubscriptionInput } from '@dukanti/shared';
import { loadPayPalSdk } from '../lib/paypal-sdk-loader';

function paypalErrorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return '';

  const record = error as Record<string, unknown>;
  return [record.err, record.message, record.name]
    .filter((value): value is string => typeof value === 'string')
    .join(' ');
}

function isPayPalWindowClosed(error: unknown): boolean {
  const text = paypalErrorText(error).toLowerCase();
  return (
    text.includes('window is closed') ||
    text.includes('window closed') ||
    text.includes('popup closed') ||
    text.includes('can not determine type')
  );
}

export function PayPalSubscriptionButton(props: {
  readonly storeId: string;
  readonly plan: BillingPlanCatalogItem;
  readonly disabled: boolean;
  readonly startSubscription: (input: StartSubscriptionInput) => Promise<{
    intentId: string;
    paypalPlanId: string;
    customId: string;
  }>;
  readonly confirmSubscription: (input: ConfirmSubscriptionInput) => Promise<void>;
  readonly onPending: () => void;
  readonly onApproved?: () => void;
  readonly onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const generatedId = useId().replace(/:/g, '');
  const containerId = `paypal-button-${generatedId}`;
  const activeIntentId = useRef<string | null>(null);
  const latestProps = useRef(props);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'closed' | 'error'>('idle');

  const reportError = useCallback((error: unknown): void => {
    setState('error');
    latestProps.current.onError(error);
  }, []);

  useEffect(() => {
    latestProps.current = props;
  }, [props]);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (container) container.innerHTML = '';
    activeIntentId.current = null;

    if (props.disabled || !env.VITE_PAYPAL_CLIENT_ID) {
      setState('idle');
      return undefined;
    }

    let cancelled = false;
    setState('loading');

    void loadPayPalSdk({ clientId: env.VITE_PAYPAL_CLIENT_ID, locale: lang })
      .then((paypal) => {
        if (cancelled) return undefined;
        const button = paypal.Buttons({
          style: {
            color: 'gold',
            label: 'subscribe',
            layout: 'vertical',
            shape: 'rect',
            tagline: false,
          },
          createSubscription: async (_data, actions) => {
            const current = latestProps.current;
            try {
              const intent = await current.startSubscription({
                storeId: current.storeId,
                planKey: current.plan.planKey,
                interval: current.plan.interval,
              });
              activeIntentId.current = intent.intentId;
              return await actions.subscription.create({
                plan_id: intent.paypalPlanId,
                custom_id: intent.customId,
              });
            } catch (error) {
              activeIntentId.current = null;
              reportError(error);
              throw error;
            }
          },
          onApprove: async (data) => {
            const current = latestProps.current;
            current.onPending();
            if (!data.subscriptionID) {
              reportError({ messageKey: 'admin.billing.errors.paypalUnavailable' });
              return;
            }
            const intentId = activeIntentId.current;
            if (!intentId) {
              reportError({ messageKey: 'admin.billing.errors.paypalUnavailable' });
              return;
            }
            try {
              await current.confirmSubscription({
                storeId: current.storeId,
                intentId,
                paypalSubscriptionId: data.subscriptionID,
              });
              current.onApproved?.();
            } catch (error) {
              reportError(error);
              throw error;
            }
          },
          onCancel: () => {
            activeIntentId.current = null;
            setState('closed');
          },
          onError: (error) => {
            if (isPayPalWindowClosed(error)) {
              activeIntentId.current = null;
              setState('closed');
              return;
            }
            reportError(error);
          },
        });
        return button.render(`#${containerId}`);
      })
      .then(() => {
        if (!cancelled) setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isPayPalWindowClosed(error)) {
          activeIntentId.current = null;
          setState('closed');
          return;
        }
        reportError(error);
      });

    return () => {
      cancelled = true;
      activeIntentId.current = null;
      const currentContainer = document.getElementById(containerId);
      if (currentContainer) currentContainer.innerHTML = '';
    };
  }, [
    containerId,
    lang,
    props.disabled,
    props.plan.interval,
    props.plan.planKey,
    props.storeId,
    reportError,
  ]);

  if (!env.VITE_PAYPAL_CLIENT_ID) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t('admin.billing.errors.configurationMissing')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {state === 'loading' ? (
        <p className="text-sand-500 flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t('admin.billing.paypal.loading')}
        </p>
      ) : null}
      {state === 'error' ? (
        <p role="alert" className="text-destructive text-sm">
          {t('admin.billing.errors.paypalUnavailable')}
        </p>
      ) : null}
      {state === 'closed' ? (
        <p role="status" className="text-sand-600 text-sm">
          {t('admin.billing.paypal.closed')}
        </p>
      ) : null}
      <div id={containerId} data-testid="paypal-subscription-button" />
    </div>
  );
}
